/**
 * Clasificacion del evento.
 *
 * Tabla de verdad, con encabezados asociados a las celdas, y no una rejilla de
 * divs: es lo unico que un lector de pantalla puede recorrer diciendo "fila 3,
 * puntos 15".
 *
 * Solo aparece el nombre del equipo. El resto de datos del registro son
 * personales y no salen de la zona de administracion.
 *
 * En movil la tabla no se convierte en tarjetas apiladas: se conserva la tabla
 * y se deja desplazar en horizontal dentro de su caja. Convertirla romperia la
 * relacion entre encabezado y celda, que es justo lo que la hace legible con
 * lector de pantalla, y una clasificacion se lee comparando columnas.
 */

import { useTranslation } from 'react-i18next';
import { useRanking, useSesion } from '@/api/consultas.ts';
import { etiquetaFormato } from '@/i18n';
import { MensajeError } from '@/componentes/MensajeError.tsx';

function formatearDuracion(segundos: number | null, formato: string): string {
  if (segundos === null) return '—';
  const minutos = Math.floor(segundos / 60);
  const resto = segundos % 60;
  const numero = new Intl.NumberFormat(formato, { minimumIntegerDigits: 2 });
  return `${numero.format(minutos)}:${numero.format(resto)}`;
}

export function Clasificacion() {
  const { t } = useTranslation();
  const formato = etiquetaFormato();

  const consultaSesion = useSesion();
  const eventoId = consultaSesion.data?.evento.id ?? null;
  const enCurso = consultaSesion.data?.evento.en_curso ?? false;
  const consulta = useRanking(eventoId, enCurso);

  if (!eventoId) {
    return (
      <div className="contenedor py-10 sm:py-16">
        <div className="tarjeta mx-auto max-w-xl text-center">
          <h1 className="titulo-seccion">{t('sessio.sensesessio')}</h1>
          <p className="mt-3 text-tinta-700">{t('sessio.sensesessioDetall')}</p>
        </div>
      </div>
    );
  }

  if (consulta.isError) {
    return (
      <div className="contenedor py-8">
        <MensajeError error={consulta.error} alReintentar={() => void consulta.refetch()} />
      </div>
    );
  }

  const propioId = consultaSesion.data?.participante_id;

  return (
    <div className="contenedor space-y-5 py-8 sm:py-12">
      <header>
        <p className="antetitulo">{t('recorregut.antetitol')}</p>
        <div className="mt-1.5 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <h1 className="titulo-pagina">{t('classificacio.titol')}</h1>
          <p
            className={`distintivo ${
              consulta.data?.evento.en_curso ? 'distintivo-activo' : 'distintivo-neutro'
            }`}
          >
            {consulta.data?.evento.en_curso ? t('classificacio.enCurs') : t('classificacio.acabat')}
          </p>
        </div>
        <div className="filete" />
      </header>

      {consulta.isPending ? (
        <p role="status" className="text-tinta-500">
          {t('app.carregant')}
        </p>
      ) : !consulta.data || consulta.data.filas.length === 0 ? (
        <p className="estado-vacio text-tinta-500">{t('classificacio.senseDades')}</p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-crema-200 bg-white">
            <table className="tabla min-w-[34rem]">
              <caption className="sr-only">
                {t('classificacio.titol')} — {consulta.data.evento.nombre}
              </caption>
              <thead>
                <tr>
                  <th scope="col" className="w-14">
                    {t('classificacio.posicio')}
                  </th>
                  <th scope="col">{t('classificacio.equip')}</th>
                  <th scope="col" className="text-right">
                    {t('classificacio.punts')}
                  </th>
                  <th scope="col" className="text-right">
                    {t('classificacio.encerts')}
                  </th>
                  <th scope="col" className="text-right">
                    {t('classificacio.durada')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {consulta.data.filas.map((fila) => {
                  const esPropio = fila.participante_id === propioId;
                  return (
                    <tr key={fila.participante_id} className={esPropio ? 'bg-roig-50' : undefined}>
                      <th scope="row" className="px-3 py-3">
                        {/*
                          El sello del puesto, el mismo objeto que numera los
                          puntos en el mapa y en la lista. Los tres primeros van
                          rellenos; del cuarto en adelante, con filete. Es
                          jerarquia visual, no informacion: el numero se lee
                          igual en los dos casos.
                        */}
                        <span
                          className={`sello text-xs ${
                            fila.posicion <= 3
                              ? 'border-roig-600 bg-roig-600 text-white'
                              : 'border-crema-200 bg-white text-tinta-700'
                          }`}
                          style={{ inlineSize: '2rem', blockSize: '2rem' }}
                        >
                          {new Intl.NumberFormat(formato).format(fila.posicion)}
                        </span>
                      </th>
                      <td className="font-medium text-tinta-900">
                        {fila.nombre}
                        {esPropio ? (
                          <span className="distintivo distintivo-activo ml-2">
                            {t('classificacio.vosaltres')}
                          </span>
                        ) : null}
                      </td>
                      <td className="text-right font-titular text-base font-semibold text-tinta-900">
                        {new Intl.NumberFormat(formato).format(fila.puntos)}
                      </td>
                      <td className="text-right">
                        {new Intl.NumberFormat(formato).format(fila.aciertos)}
                      </td>
                      <td className="text-right tabular-nums">
                        {formatearDuracion(fila.duracion_segundos, formato)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-tinta-500">
              {t('classificacio.actualitzat', {
                hora: new Intl.DateTimeFormat(formato, { timeStyle: 'medium' }).format(
                  new Date(consulta.data.calculado_en),
                ),
              })}
            </p>
            <button
              type="button"
              className="boton-secundario"
              onClick={() => void consulta.refetch()}
            >
              {t('classificacio.actualitzar')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
